import type { ScreenContext } from "../../shell/types";
import { api, el, toast } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { openModal } from "../../ui/modal";
import { ib, table } from "../../ui/record";

// Home Care › Settings › Home and routine (brief 1E-17, mockup v76): the sections every patient's
// guide holds (on or off, Add a section), and when caregivers see it: Before you start after n
// days away, the nurse's review every n days, photo consent before a patient's photo. Each change
// saves at once (Settings at Edit; everyone with Settings at View reads it). Brief 1E-18: the
// orientation checklist built from the guide (both people check), and Running late? after n
// minutes.

interface Answer {
  firstVisitDays: number;
  reviewDays: number;
  photoConsentRequired: boolean;
  orientationChecklist: boolean;
  runningLate: boolean;
  sections: { id: string; name: string; icon: string; isOn: boolean }[];
  error?: string;
}

export async function routineSettingsScreen(host: HTMLElement, ctx: ScreenContext) {
  ctx.crumb("Home and routine");
  const slot = el("div");
  host.append(
    el(
      "div",
      { class: "head" },
      el(
        "div",
        { class: "t" },
        el("div", { class: "eyebrow" }, "Home Care settings"),
        el("h1", {}, icon("home", "h1i"), "Home and routine"),
        el(
          "div",
          { class: "muted" },
          "What each patient's guide holds, and when caregivers see it.",
        ),
      ),
    ),
    slot,
  );
  let a: Answer;

  async function load() {
    const res = await api<Answer>("GET", "/api/settings/home-routine");
    if (!res.ok) {
      slot.replaceChildren(
        el(
          "p",
          { class: "note", role: "alert" },
          "Home and routine settings need Settings at View.",
        ),
      );
      return;
    }
    a = res.body;
    draw();
  }

  const took = (r: { ok: boolean; body: Answer }, ok = "Saved") => {
    if (!r.ok) {
      toast("That wasn't saved", r.body.error);
      draw();
      return false;
    }
    a = r.body;
    draw();
    toast(ok);
    return true;
  };

  function addSection() {
    const name = el("input", { id: "rs-name", type: "text", maxlength: "40" });
    const save = el("button", { type: "button", class: "btn pri" }, icon("check"), " Add");
    const cancel = el("button", { type: "button", class: "btn" }, "Cancel");
    const m = openModal({
      title: "Add a section",
      body: [
        el(
          "form",
          { class: "form" },
          el("div", { class: "field" }, el("label", { for: "rs-name" }, "Name"), name),
        ),
      ],
      actions: [save, cancel],
    });
    cancel.addEventListener("click", () => m.close());
    save.addEventListener("click", async () => {
      const r = await api<Answer>("POST", "/api/settings/routine-sections", { name: name.value });
      if (!r.ok) {
        m.note.hidden = false;
        m.note.replaceChildren(r.body.error ?? "That wasn't saved.");
        return;
      }
      m.close();
      took(r, "Section added");
    });
    name.focus();
  }

  function draw() {
    const sw = (s: Answer["sections"][number]) => {
      const input = el("input", { type: "checkbox", checked: s.isOn, "aria-label": s.name });
      input.addEventListener("change", async () =>
        took(
          await api<Answer>("PUT", `/api/settings/routine-sections/${s.id}`, {
            isOn: input.checked,
          }),
        ),
      );
      return el("label", { class: "switch-l" }, input, el("span"));
    };
    const num = (key: "firstVisitDays" | "reviewDays", label: string, max: number) => {
      const i = el("input", {
        type: "number",
        id: `rs-${key}`,
        min: "1",
        max: String(max),
        value: String(a[key]),
        "aria-label": label,
        style: "width:70px;display:inline-block;margin:0 4px",
      });
      i.addEventListener(
        "change",
        async () =>
          void took(
            await api<Answer>("PUT", "/api/settings/home-routine", { [key]: Number(i.value) }),
          ),
      );
      return i;
    };
    const box = (key: "photoConsentRequired" | "orientationChecklist" | "runningLate") => {
      const i = el("input", { type: "checkbox", id: `rs-${key}`, checked: a[key] });
      i.addEventListener(
        "change",
        async () =>
          void took(await api<Answer>("PUT", "/api/settings/home-routine", { [key]: i.checked })),
      );
      return i;
    };
    slot.replaceChildren(
      el(
        "div",
        { class: "grid2", style: "align-items:start" },
        el(
          "section",
          { class: "card", "aria-label": "Sections" },
          el(
            "div",
            { class: "cardhead" },
            el("h2", {}, "Sections"),
            ib("plus", "Add a section", addSection),
          ),
          table(
            [
              {
                h: "Section",
                f: (s: Answer["sections"][number]) =>
                  el("span", {}, icon(s.icon), " ", el("b", {}, s.name)),
              },
              { h: "", f: sw },
            ],
            a.sections,
            undefined,
            "Sections",
          ),
        ),
        el(
          "section",
          { class: "card", "aria-label": "When caregivers see it" },
          el("h2", {}, "When caregivers see it"),
          el(
            "form",
            { class: "form" },
            el(
              "label",
              { class: "ptask" },
              el(
                "span",
                {},
                "Show Before you start on a first visit, or after",
                num("firstVisitDays", "Days away", 365),
                "days away",
              ),
            ),
            el(
              "label",
              { class: "ptask" },
              el(
                "span",
                {},
                "Remind the nurse to review each guide every",
                num("reviewDays", "Days between reviews", 730),
                "days",
              ),
            ),
            el(
              "label",
              { class: "ptask", for: "rs-photoConsentRequired" },
              box("photoConsentRequired"),
              " Photo consent before a patient's photo",
            ),
            el(
              "label",
              { class: "ptask", for: "rs-orientationChecklist" },
              box("orientationChecklist"),
              " Orientation checklist built from the guide; both people check",
            ),
            el(
              "label",
              { class: "ptask", for: "rs-runningLate" },
              box("runningLate"),
              " Running late: offer a text to the patient once the visit is late",
            ),
          ),
        ),
      ),
    );
  }

  await load();
}
